import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { SEO, StructuredData } from '@/components/seo'

export default function SecurityPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <SEO
        title="Security - Enterprise-Grade Data Protection"
        description="Your candidate data is sensitive. JumpstoneHR uses AES-256 encryption, TLS 1.3, SOC 2 compliant infrastructure, and strict access controls. Your data is never used to train AI models."
        canonical="/security"
        structuredData={[
          StructuredData.webPage(
            'Security at JumpstoneHR',
            'Enterprise-grade security for your hiring data. Encryption, access controls, and compliance.',
            '/security'
          ),
          StructuredData.breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Security', url: '/security' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'JumpstoneHR Security',
            description: 'Enterprise-grade security practices and compliance information.',
            about: {
              '@type': 'Thing',
              name: 'Data Security',
              description: 'Information about JumpstoneHR security practices including encryption, access control, and compliance.',
            },
            mentions: [
              { '@type': 'Thing', name: 'AES-256 Encryption' },
              { '@type': 'Thing', name: 'TLS 1.3' },
              { '@type': 'Thing', name: 'SOC 2 Type II' },
              { '@type': 'Thing', name: 'PIPEDA' },
              { '@type': 'Thing', name: 'GDPR' },
            ],
          },
        ]}
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          {/* Gradient orb */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-500/20 via-brand-600/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          
          <div className="relative mx-auto max-w-4xl section-padding">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px flex-1 max-w-[60px] bg-brand-500" />
              <span className="text-brand-400 text-sm font-medium tracking-widest uppercase">Trust & Security</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-8">
              Security you can{' '}
              <span className="text-brand-400">trust</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-surface-300 font-light max-w-2xl">
              Your candidate data is sensitive. We treat it that way. Here's how we keep your information secure.
            </p>
          </div>
        </section>

        {/* Security Practices */}
        <section className="py-20 bg-surface-900">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Infrastructure</span>
              <h2 className="text-3xl font-bold mt-3 text-white">Enterprise-grade security</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Encryption at Rest</h3>
                <p className="text-surface-400">
                  All data stored in our systems is encrypted using AES-256 encryption. Your candidate information is never stored in plaintext.
                </p>
              </div>

              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Encryption in Transit</h3>
                <p className="text-surface-400">
                  All connections to JumpstoneHR use TLS 1.3 encryption. We enforce HTTPS everywhere with HSTS preloading.
                </p>
              </div>

              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Cloud Infrastructure</h3>
                <p className="text-surface-400">
                  We host on industry-leading cloud providers with SOC 2 Type II certification. Data is stored in Canadian data centers.
                </p>
              </div>

              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Daily Backups</h3>
                <p className="text-surface-400">
                  Automated daily backups with point-in-time recovery. Backups are encrypted and stored in geographically separate locations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Access Control */}
        <section className="py-24 bg-surface-950">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Access Control</span>
              <h2 className="text-3xl font-bold mt-3 text-white">Who can access your data</h2>
            </div>

            <div className="space-y-8">
              <div className="bg-surface-900 border border-surface-800 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-white mb-4">Your Team</h3>
                <ul className="space-y-3 text-surface-300">
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Role-based access control (RBAC) for team members</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Granular permissions for viewing, editing, and managing candidates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Audit logs for all user actions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Single sign-on (SSO) support for enterprise plans</span>
                  </li>
                </ul>
              </div>

              <div className="bg-surface-900 border border-surface-800 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-white mb-4">Jumpstone Staff</h3>
                <ul className="space-y-3 text-surface-300">
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Minimal access principle: staff only access data when necessary for support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>All staff access is logged and auditable</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Background checks for all employees with data access</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-brand-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Mandatory security training for all team members</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* AI & Data */}
        <section className="py-24 bg-surface-900">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">AI & Data</span>
              <h2 className="text-3xl font-bold mt-3 text-white">How we use your data with AI</h2>
            </div>

            <div className="bg-surface-950 border border-surface-800 rounded-2xl p-8 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Your data is never used to train our models</h3>
                  <p className="text-surface-400">
                    We do not use your candidate data, job descriptions, or hiring decisions to train our AI models. Your data remains yours.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Data Isolation</h3>
                <p className="text-surface-400">
                  Each customer's data is logically isolated. Your candidate information is never mixed with or accessible by other customers.
                </p>
              </div>
              
              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Processing Transparency</h3>
                <p className="text-surface-400">
                  All AI-generated recommendations include clear reasoning. You can always see why the AI made a particular suggestion.
                </p>
              </div>
              
              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Data Retention Control</h3>
                <p className="text-surface-400">
                  You control how long data is retained. Delete candidates or close roles, and the associated data is removed from our systems.
                </p>
              </div>
              
              <div className="bg-surface-950 border border-surface-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Export Anytime</h3>
                <p className="text-surface-400">
                  Export all your data at any time in standard formats. No lock-in, no friction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section className="py-24 bg-surface-950">
          <div className="mx-auto max-w-4xl section-padding">
            <div className="mb-12">
              <span className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Compliance</span>
              <h2 className="text-3xl font-bold mt-3 text-white">Regulatory compliance</h2>
            </div>

            <div className="grid gap-6">
              <div className="bg-surface-900 border border-surface-800 rounded-xl p-6 flex items-start gap-6">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-400 font-bold text-lg">PIPEDA</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Personal Information Protection and Electronic Documents Act</h3>
                  <p className="text-surface-400">
                    As a Canadian company, we comply with PIPEDA and applicable provincial privacy legislation. We follow the 10 fair information principles for handling personal data.
                  </p>
                </div>
              </div>

              <div className="bg-surface-900 border border-surface-800 rounded-xl p-6 flex items-start gap-6">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-400 font-bold text-lg">GDPR</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">General Data Protection Regulation</h3>
                  <p className="text-surface-400">
                    We support GDPR compliance for customers processing data of EU residents. This includes data subject rights, lawful processing, and appropriate safeguards for international transfers.
                  </p>
                </div>
              </div>

              <div className="bg-surface-900 border border-surface-800 rounded-xl p-6 flex items-start gap-6">
                <div className="w-14 h-14 rounded-xl bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-400 font-bold text-sm">SOC 2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">SOC 2 Type II (In Progress)</h3>
                  <p className="text-surface-400">
                    We are working toward SOC 2 Type II certification to provide independent assurance of our security controls. Contact us for our current security posture documentation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-24 bg-surface-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-t from-brand-500/10 via-brand-600/5 to-transparent rounded-full blur-3xl" />
          </div>
          
          <div className="relative mx-auto max-w-4xl section-padding text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Have security questions?
            </h2>
            <p className="text-xl text-surface-300 mb-8 max-w-2xl mx-auto">
              We're happy to discuss our security practices in detail, complete security questionnaires, or schedule a call with our team.
            </p>
            <a 
              href="mailto:security@jumpstonehr.com" 
              className="inline-flex items-center gap-2 btn bg-brand-600 text-white hover:bg-brand-500 px-8 py-4 text-lg"
            >
              security@jumpstonehr.com
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

